import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import { PropertyChecklistStack } from '../components/property-checklist-stack.tsx';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, propertyApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, LoadingBlock, Select, TextArea, TextInput } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { useLocalizedContent } from '../i18n/localized.ts';
import { formatLongDate } from '../i18n/datetime.ts';
import { SERVICE_FREQUENCIES, type ServiceFrequency } from '@shared/constants/index.ts';
import type { PropertyChecklistItem } from '@shared/types/index.ts';

const EMPTY_FORM = {
  areaName: '',
  areaEs: '',
  taskName: '',
  taskEs: '',
  description: '',
  descriptionEs: '',
  frequency: 'every_visit' as ServiceFrequency,
  requiresPhoto: false,
};

export default function PropertyChecklistPage() {
  const { t } = useTranslation();
  const { customerId = '', propertyId = '' } = useParams();
  const { can } = useSession();
  const hasScheduling = usePlanFeature('scheduling');
  const hasReports = usePlanFeature('checklist_reports');
  const localized = useLocalizedContent();
  const queryClient = useQueryClient();
  const writable = can('customers:write');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [quoteId, setQuoteId] = useState('');

  const property = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => propertyApi.get(propertyId),
    enabled: hasScheduling && Boolean(propertyId),
  });
  const checklist = useQuery({
    queryKey: ['property-checklist', propertyId],
    queryFn: () => propertyApi.checklist(propertyId),
    enabled: hasScheduling && Boolean(propertyId),
  });
  const history = useQuery({
    queryKey: ['property-checklist-history', propertyId],
    queryFn: () => propertyApi.checklistHistory(propertyId),
    enabled: hasScheduling && Boolean(propertyId),
  });
  const quotes = useQuery({
    queryKey: ['customer-quotes-for-checklist', customerId],
    queryFn: () => customerApi.quotes(customerId, { page: 1, pageSize: 100 }),
    enabled: hasScheduling && writable && Boolean(customerId),
  });

  useDocumentTitle(t('checklists.pageTitle'));

  const list = checklist.data?.checklist ?? null;
  const accepted = (quotes.data?.items ?? []).filter(
    (quote) =>
      (quote.status === 'accepted' || quote.status === 'completed') &&
      (quote.propertyId === null || quote.propertyId === propertyId),
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['property-checklist', propertyId] });
  };

  const fail = (caught: unknown) =>
    setError(caught instanceof ApiRequestError ? caught.message : t('checklists.saveError'));

  const addItem = useMutation({
    mutationFn: () =>
      propertyApi.addChecklistItem(propertyId, {
        version: list?.version,
        areaName: form.areaName.trim(),
        areaNameTranslations: form.areaEs.trim() ? { es: form.areaEs.trim() } : null,
        taskName: form.taskName.trim(),
        taskNameTranslations: form.taskEs.trim() ? { es: form.taskEs.trim() } : null,
        description: form.description.trim() ? form.description.trim() : null,
        descriptionTranslations: form.descriptionEs.trim() ? { es: form.descriptionEs.trim() } : null,
        frequency: form.frequency,
        requiresPhoto: form.requiresPhoto,
      }),
    onSuccess: async () => {
      setError(null);
      setForm(EMPTY_FORM);
      setAdding(false);
      await refresh();
    },
    onError: fail,
  });

  const saveItem = useMutation({
    mutationFn: (item: PropertyChecklistItem) =>
      propertyApi.updateChecklistItem(propertyId, item.id, {
        version: list?.version ?? 1,
        areaName: form.areaName.trim(),
        areaNameTranslations: form.areaEs.trim() ? { es: form.areaEs.trim() } : null,
        taskName: form.taskName.trim(),
        taskNameTranslations: form.taskEs.trim() ? { es: form.taskEs.trim() } : null,
        description: form.description.trim() ? form.description.trim() : null,
        descriptionTranslations: form.descriptionEs.trim() ? { es: form.descriptionEs.trim() } : null,
        frequency: form.frequency,
        requiresPhoto: form.requiresPhoto,
      }),
    onSuccess: async () => {
      setError(null);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await refresh();
    },
    onError: fail,
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => propertyApi.deleteChecklistItem(propertyId, itemId, list?.version ?? 1),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  const setOrder = useMutation({
    mutationFn: (enforceOrder: boolean) =>
      propertyApi.updateChecklist(propertyId, { version: list?.version ?? 1, enforceOrder }),
    onSuccess: refresh,
    onError: fail,
  });

  const rename = useMutation({
    mutationFn: () =>
      propertyApi.updateChecklist(propertyId, {
        version: list?.version ?? 1,
        name: name.trim(),
        nameTranslations: nameEs.trim() ? { es: nameEs.trim() } : null,
      }),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  const fromQuote = useMutation({
    mutationFn: () => propertyApi.checklistFromQuote(propertyId, quoteId, list?.version),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  const fromTemplates = useMutation({
    mutationFn: () => propertyApi.checklistFromTemplates(propertyId, list?.version),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: fail,
  });

  const startEdit = (item: PropertyChecklistItem) => {
    setEditingId(item.id);
    setAdding(false);
    setForm({
      areaName: item.areaName,
      areaEs: item.areaNameTranslations?.es ?? '',
      taskName: item.taskName,
      taskEs: item.taskNameTranslations?.es ?? '',
      description: item.description ?? '',
      descriptionEs: item.descriptionTranslations?.es ?? '',
      frequency: item.frequency,
      requiresPhoto: item.requiresPhoto,
    });
  };

  const propertyLabel = property.data?.name ?? property.data?.addressLine1 ?? t('checklists.pageTitle');

  if (!hasScheduling) return <PlanRequiredPage feature="scheduling" title={t('checklists.pageTitle')} />;

  return (
    <WorkPage
      title={t('checklists.pageTitle')}
      description={propertyLabel}
      actions={
        <Link to={`/customers/${customerId}`} className="btn btn-secondary">
          {t('checklists.backToCustomer')}
        </Link>
      }
    >
      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      <div className="mb-6">
        <PropertyChecklistStack key={propertyId} propertyId={propertyId} customerId={customerId} />
      </div>
      {checklist.isLoading ? <LoadingBlock /> : null}
      {checklist.isError ? (
        <Alert tone="error">
          {t('checklistStack.loadError')}
          <Button className="mt-3" onClick={() => void checklist.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}
      {checklist.data && !list ? (
        <WorkEmpty title={t('checklists.pageTitle')}>
          <p>{t('checklists.empty')}</p>
        </WorkEmpty>
      ) : null}

      {list && writable ? (
        <label className="mb-4 flex items-center gap-2">
          <input
            type="checkbox"
            checked={list.enforceOrder ?? false}
            disabled={setOrder.isPending || !hasReports}
            onChange={(event) => setOrder.mutate(event.target.checked)}
          />
          {t('checklists.lockOrder')}
        </label>
      ) : null}

      {writable ? (
        <div className="card mb-6 flex flex-wrap items-end gap-3">
          {accepted.length > 0 ? (
            <>
              <Select
                label={t('checklists.quoteLabel')}
                value={quoteId}
                onChange={(event) => setQuoteId(event.target.value)}
                options={[
                  { value: '', label: t('checklists.quotePlaceholder') },
                  ...accepted.map((quote) => ({ value: quote.id, label: quote.quoteNumber })),
                ]}
              />
              <Button
                disabled={!quoteId || fromQuote.isPending}
                onClick={() => {
                  if (window.confirm(t('checklists.replaceConfirm'))) fromQuote.mutate();
                }}
              >
                {t('checklists.fromQuote')}
              </Button>
            </>
          ) : null}
          <Button
            variant="secondary"
            disabled={fromTemplates.isPending}
            onClick={() => {
              if (window.confirm(t('checklists.replaceConfirm'))) fromTemplates.mutate();
            }}
          >
            {t('checklists.fromTemplates')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
              setForm(EMPTY_FORM);
            }}
          >
            {t('checklists.addTask')}
          </Button>
        </div>
      ) : null}

      {list ? (
        <section className="card mb-6">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <TextInput
              label={t('checklists.nameLabel')}
              value={name || localized.text(list.name, list.nameTranslations)}
              onChange={(event) => setName(event.target.value)}
              disabled={!writable}
            />
            <TextInput
              label={t('checklists.nameEsLabel')}
              value={nameEs || list.nameTranslations?.es || ''}
              onChange={(event) => setNameEs(event.target.value)}
              disabled={!writable}
            />
            {writable ? (
              <Button disabled={rename.isPending || !(name || nameEs)} onClick={() => rename.mutate()}>
                {t('checklists.saveName')}
              </Button>
            ) : null}
          </div>
          <ul className="divide-y divide-[color:var(--color-line)]">
            {list.items.map((item) => (
              <li key={item.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div>
                  <p className="font-medium">
                    {localized.text(item.areaName, item.areaNameTranslations)}
                    {' · '}
                    {localized.text(item.taskName, item.taskNameTranslations)}
                  </p>
                  <p className="text-sm text-[color:var(--color-muted)]">
                    {t(`servicesStep.frequency.${item.frequency}`)}
                    {item.requiresPhoto ? ` · ${t('checklists.photoRequired')}` : ''}
                  </p>
                </div>
                {writable ? (
                  <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => startEdit(item)}>
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="danger"
                      disabled={removeItem.isPending}
                      onClick={() => {
                        const task = `${localized.text(item.areaName, item.areaNameTranslations)} · ${localized.text(item.taskName, item.taskNameTranslations)}`;
                        if (window.confirm(t('checklists.removeTaskConfirm', { task }))) removeItem.mutate(item.id);
                      }}
                    >
                      {t('common.remove')}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {adding || editingId ? (
        <TaskForm
          form={form}
          setForm={setForm}
          hasReports={hasReports}
          busy={addItem.isPending || saveItem.isPending}
          onCancel={() => {
            setAdding(false);
            setEditingId(null);
            setForm(EMPTY_FORM);
          }}
          onSave={() => {
            if (editingId && list) {
              const item = list.items.find((entry) => entry.id === editingId);
              if (item) saveItem.mutate(item);
              return;
            }
            addItem.mutate();
          }}
        />
      ) : null}

      <section className="card" aria-labelledby="checklist-history-heading">
        <h2 id="checklist-history-heading" className="mb-3 text-lg font-semibold">
          {t('checklists.historyHeading')}
        </h2>
        {history.data && history.data.visits.length === 0 ? (
          <p className="text-sm text-[color:var(--color-muted)]">{t('checklists.historyEmpty')}</p>
        ) : null}
        <ul className="space-y-4">
          {(history.data?.visits ?? []).map((visit) => (
            <li key={visit.appointmentId}>
              <p className="font-medium">
                {formatLongDate(visit.scheduledDate)} · {visit.customerName} ·{' '}
                {t('checklists.progress', { completed: visit.progress.completed, total: visit.progress.total })}
              </p>
              <ul className="mt-1 space-y-1 text-sm">
                {visit.items.map((item) => (
                  <li key={item.id}>
                    {localized.text(item.taskName, item.taskNameTranslations)} · {t(`checklists.status.${item.status}`)}
                    {item.completedByName ? ` · ${item.completedByName}` : ''}
                    {item.note ? ` · ${item.note}` : ''}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </section>
    </WorkPage>
  );
}

function TaskForm({
  form,
  setForm,
  hasReports,
  busy,
  onCancel,
  onSave,
}: {
  form: typeof EMPTY_FORM;
  setForm: (next: typeof EMPTY_FORM) => void;
  hasReports: boolean;
  busy: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  return (
    <form
      className="card mb-6 grid gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <TextInput
          label={t('checklists.areaLabel')}
          value={form.areaName}
          required
          onChange={(event) => setForm({ ...form, areaName: event.target.value })}
        />
        <TextInput
          label={t('checklists.areaEsLabel')}
          value={form.areaEs}
          onChange={(event) => setForm({ ...form, areaEs: event.target.value })}
        />
        <TextInput
          label={t('checklists.taskLabel')}
          value={form.taskName}
          required
          onChange={(event) => setForm({ ...form, taskName: event.target.value })}
        />
        <TextInput
          label={t('checklists.taskEsLabel')}
          value={form.taskEs}
          onChange={(event) => setForm({ ...form, taskEs: event.target.value })}
        />
      </div>
      <TextArea
        label={t('checklists.descriptionLabel')}
        rows={2}
        value={form.description}
        onChange={(event) => setForm({ ...form, description: event.target.value })}
      />
      <TextArea
        label={t('checklists.descriptionEsLabel')}
        rows={2}
        value={form.descriptionEs}
        onChange={(event) => setForm({ ...form, descriptionEs: event.target.value })}
      />
      <Select
        label={t('checklists.frequencyLabel')}
        value={form.frequency}
        onChange={(event) => setForm({ ...form, frequency: event.target.value as ServiceFrequency })}
        options={SERVICE_FREQUENCIES.map((frequency) => ({
          value: frequency,
          label: t(`servicesStep.frequency.${frequency}`),
        }))}
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.requiresPhoto}
          disabled={!hasReports}
          onChange={(event) => setForm({ ...form, requiresPhoto: event.target.checked })}
        />
        {t('checklists.requiresPhoto')}
      </label>
      {!hasReports ? <p className="text-sm text-[color:var(--color-muted)]">{t('checklists.photoPlan')}</p> : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || !form.areaName.trim() || !form.taskName.trim()}>
          {t('checklists.saveTask')}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
